/**
 * Native PDP: gallery chrome, color-strip scroll, size-chart dialog,
 * and variant option sync (color/size → variant id).
 * Overflow color changes navigate via color tile links (full page).
 */
(function () {
  function selectThumb(thumbs, index) {
    thumbs.forEach((thumb, i) => {
      const on = i === index;
      thumb.classList.toggle('is-selected', on);
      thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const activeThumb = thumbs[index];
    const thumbsEl = activeThumb?.closest('[data-ak-thumbs]');
    if (activeThumb && thumbsEl) {
      const isColumn = getComputedStyle(thumbsEl).flexDirection === 'column';
      if (isColumn) {
        const thumbTop = activeThumb.offsetTop;
        const thumbBottom = thumbTop + activeThumb.offsetHeight;
        const viewTop = thumbsEl.scrollTop;
        const viewBottom = viewTop + thumbsEl.clientHeight;
        if (thumbTop < viewTop) {
          thumbsEl.scrollTo({ top: thumbTop, behavior: 'smooth' });
        } else if (thumbBottom > viewBottom) {
          thumbsEl.scrollTo({ top: thumbBottom - thumbsEl.clientHeight, behavior: 'smooth' });
        }
      } else {
        const thumbLeft = activeThumb.offsetLeft;
        const thumbRight = thumbLeft + activeThumb.offsetWidth;
        const viewLeft = thumbsEl.scrollLeft;
        const viewRight = viewLeft + thumbsEl.clientWidth;
        if (thumbLeft < viewLeft) {
          thumbsEl.scrollTo({ left: thumbLeft, behavior: 'smooth' });
        } else if (thumbRight > viewRight) {
          thumbsEl.scrollTo({ left: thumbRight - thumbsEl.clientWidth, behavior: 'smooth' });
        }
      }
    }
  }

  function selectDots(gallery, index) {
    if (!gallery) return;
    const dots = gallery.querySelectorAll('[data-ak-gallery-dot]');
    dots.forEach((dot, i) => {
      const on = i === index;
      dot.classList.toggle('is-selected', on);
      dot.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  /**
   * Stack layout keeps the full image grid for scroll browsing. Thumb / variant
   * selection only swaps the hero figure image in place (no page jump).
   * Cache originals on each stack item so the hero slot can be restored after
   * it has been overwritten.
   */
  function cacheMediaSources(items) {
    items.forEach((item) => {
      if (item.dataset.akMediaCached === 'true') return;
      const img = item.querySelector('img');
      if (!img) return;
      item.dataset.akSrc = img.getAttribute('src') || '';
      item.dataset.akSrcset = img.getAttribute('srcset') || '';
      item.dataset.akAlt = img.getAttribute('alt') || '';
      item.dataset.akMediaCached = 'true';
    });
  }

  /** @type {Map<string, HTMLImageElement>} */
  const preloadedHero = new Map();

  function heroSizes() {
    return '(min-width: 990px) 55vw, 100vw';
  }

  function preloadHeroMedia(item, priority) {
    if (!(item instanceof HTMLElement)) return null;
    cacheMediaSources([item]);
    const mediaId = item.getAttribute('data-media-id') || item.dataset.akSrc;
    if (!mediaId) return null;
    const existing = preloadedHero.get(mediaId);
    if (existing) {
      if (priority === 'high') existing.fetchPriority = 'high';
      return existing;
    }

    const img = new Image();
    img.decoding = 'async';
    img.fetchPriority = priority === 'high' ? 'high' : 'low';
    const srcset = item.dataset.akSrcset || '';
    if (srcset) img.srcset = srcset;
    img.sizes = heroSizes();
    img.src = item.dataset.akSrc || '';
    preloadedHero.set(mediaId, img);
    return img;
  }

  function applyHeroImage(heroImg, { src, srcset, alt }) {
    if (srcset) {
      heroImg.srcset = srcset;
    } else {
      heroImg.removeAttribute('srcset');
    }
    heroImg.sizes = heroSizes();
    heroImg.loading = 'eager';
    heroImg.fetchPriority = 'high';
    if (alt) heroImg.alt = alt;
    // Set src last so the browser already has srcset/sizes for selection.
    heroImg.src = src || '';
  }

  function isDecoded(img) {
    return !!(img && img.complete && img.naturalWidth > 0);
  }

  function setHeroFromItem(items, thumbs, index) {
    if (!items.length) return;
    const next = ((index % items.length) + items.length) % items.length;
    cacheMediaSources(items);
    const hero =
      items.find((el) => el.classList.contains('ak-main-product__stack-item--hero')) || items[0];
    const source = items[next];
    if (!hero || !source) return;

    const heroImg = hero.querySelector('img');
    if (!heroImg) return;

    const mediaId = source.getAttribute('data-media-id') || '';
    if (mediaId) hero.setAttribute('data-media-id', mediaId);

    const fullSrc = source.dataset.akSrc || '';
    const fullSrcset = source.dataset.akSrcset || '';
    const alt = source.dataset.akAlt || '';
    const warmed = mediaId ? preloadedHero.get(mediaId) : null;
    const sourceImg = source.querySelector('img');

    const paintSharp = () => {
      applyHeroImage(heroImg, { src: fullSrc, srcset: fullSrcset, alt });
      hero.classList.remove('is-hero-loading');
    };

    // Paint as soon as any decoded frame exists. Do NOT require naturalWidth >= 700:
    // on mobile srcset often settles around ~390px, and a preloaded Image that is
    // already complete will never fire "load" again — that left hero stuck on
    // is-hero-loading with the previous image (thumbs appeared dead).
    if (isDecoded(warmed) || isDecoded(sourceImg)) {
      paintSharp();
      return;
    }

    if (!fullSrc) {
      hero.classList.remove('is-hero-loading');
      return;
    }

    hero.classList.add('is-hero-loading');
    const loader = preloadHeroMedia(source, 'high');
    if (!loader) {
      paintSharp();
      return;
    }

    const finish = () => {
      if (mediaId && hero.getAttribute('data-media-id') !== mediaId) return;
      paintSharp();
    };

    if (loader.complete) {
      finish();
    } else {
      loader.addEventListener('load', finish, { once: true });
      loader.addEventListener(
        'error',
        () => {
          if (mediaId && hero.getAttribute('data-media-id') !== mediaId) return;
          paintSharp();
        },
        { once: true }
      );
    }
  }

  function showStackItem(items, thumbs, index, gallery) {
    const total = items.length;
    if (!total) return;
    const next = ((index % total) + total) % total;
    // Selection first so a double-click does not feel ignored.
    selectThumb(thumbs, next);
    selectDots(gallery, next);
    setHeroFromItem(items, thumbs, next);
  }

  function warmupStackMedia(items, thumbs) {
    cacheMediaSources(items);
    // Warm likely next clicks immediately (skip index 0; already the hero).
    // Remaining images follow on a short timer so we do not fight LCP on slow nets.
    const early = Math.min(items.length, 4);
    for (let i = 1; i < early; i += 1) {
      preloadHeroMedia(items[i], i === 1 ? 'high' : 'low');
    }
    const warmRest = () => {
      for (let i = early; i < items.length; i += 1) {
        preloadHeroMedia(items[i], 'low');
      }
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(warmRest, { timeout: 2500 });
    } else {
      setTimeout(warmRest, 600);
    }
    thumbs.forEach((thumb, index) => {
      const warm = () => preloadHeroMedia(items[index], 'high');
      thumb.addEventListener('pointerenter', warm, { passive: true });
      thumb.addEventListener('focus', warm, { passive: true });
    });
  }

  function showSlide(gallery, slides, thumbs, index) {
    const total = slides.length;
    if (!total) return;
    const next = ((index % total) + total) % total;
    slides.forEach((slide, i) => {
      const on = i === next;
      slide.hidden = !on;
      slide.classList.toggle('is-active', on);
    });
    selectThumb(thumbs, next);
  }

  function activeSlideIndex(slides) {
    for (let i = 0; i < slides.length; i += 1) {
      if (slides[i].classList.contains('is-active') && !slides[i].hidden) return i;
    }
    return 0;
  }

  function updateThumbNav(rail, thumbsEl) {
    if (!rail || !thumbsEl) return;
    const prev = rail.querySelector('[data-ak-thumb-prev]');
    const next = rail.querySelector('[data-ak-thumb-next]');
    const scrollable = thumbsEl.scrollHeight > thumbsEl.clientHeight + 2;
    if (prev) prev.hidden = !scrollable;
    if (next) next.hidden = !scrollable;
    if (!scrollable) return;
    if (prev) prev.disabled = thumbsEl.scrollTop <= 2;
    if (next) {
      next.disabled =
        thumbsEl.scrollTop + thumbsEl.clientHeight >= thumbsEl.scrollHeight - 2;
    }
  }

  function updateColorStrip(strip) {
    if (!strip) return;
    const row = strip.querySelector('[data-ak-color-row]');
    const prev = strip.querySelector('[data-ak-color-prev]');
    const next = strip.querySelector('[data-ak-color-next]');
    const more = strip.closest('.ak-pdp-colors')?.querySelector('[data-ak-color-more]');
    if (!row) return;

    const scrollable = row.scrollWidth > row.clientWidth + 2;
    if (prev) {
      prev.hidden = !scrollable;
      prev.disabled = row.scrollLeft <= 2;
    }
    if (next) {
      next.hidden = !scrollable;
      next.disabled = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
    }

    if (more) {
      if (!scrollable) {
        more.hidden = true;
        more.textContent = '';
        return;
      }
      const tiles = row.querySelectorAll('.ak-pdp-color-tile');
      let visible = 0;
      const left = row.scrollLeft;
      const right = left + row.clientWidth;
      tiles.forEach((tile) => {
        const tileLeft = tile.offsetLeft;
        const tileRight = tileLeft + tile.offsetWidth;
        if (tileRight > left + 4 && tileLeft < right - 4) visible += 1;
      });
      const hiddenCount = Math.max(0, tiles.length - visible);
      if (hiddenCount > 0) {
        more.hidden = false;
        more.textContent = '+' + hiddenCount + ' more';
      } else {
        more.hidden = true;
        more.textContent = '';
      }
    }
  }

  function bindThumbRail(gallery) {
    const rail = gallery.querySelector('[data-ak-thumb-rail]');
    const thumbsEl = gallery.querySelector('[data-ak-thumbs]');
    if (!rail || !thumbsEl) return;

    const thumbPrev = rail.querySelector('[data-ak-thumb-prev]');
    const thumbNext = rail.querySelector('[data-ak-thumb-next]');
    const step = () => Math.max(72, Math.floor(thumbsEl.clientHeight * 0.75));
    if (thumbPrev) {
      thumbPrev.addEventListener('click', () => {
        thumbsEl.scrollBy({ top: -step(), behavior: 'smooth' });
      });
    }
    if (thumbNext) {
      thumbNext.addEventListener('click', () => {
        thumbsEl.scrollBy({ top: step(), behavior: 'smooth' });
      });
    }
    thumbsEl.addEventListener('scroll', () => updateThumbNav(rail, thumbsEl), {
      passive: true,
    });
    window.addEventListener('resize', () => updateThumbNav(rail, thumbsEl));
    updateThumbNav(rail, thumbsEl);
  }

  function bindHeroSwipe(gallery, items, thumbs) {
    const hero =
      gallery.querySelector('.ak-main-product__stack-item--hero') || items[0];
    if (!hero || items.length < 2) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    hero.addEventListener('pointerdown', (e) => {
      if (!isMobileGallery()) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      tracking = true;
      startX = e.clientX;
      startY = e.clientY;
    });

    const end = (e) => {
      if (!tracking) return;
      tracking = false;
      if (!isMobileGallery()) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
      const selected = thumbs.findIndex((t) => t.classList.contains('is-selected'));
      const current = selected >= 0 ? selected : 0;
      const next = dx < 0 ? current + 1 : current - 1;
      if (next < 0 || next >= items.length) return;
      showStackItem(items, thumbs, next, gallery);
    };

    hero.addEventListener('pointerup', end);
    hero.addEventListener('pointercancel', () => {
      tracking = false;
    });
  }

  function isMobileGallery() {
    return window.matchMedia('(max-width: 798px)').matches;
  }

  function initStackGallery(gallery) {
    const items = Array.from(gallery.querySelectorAll('[data-ak-stack-item]'));
    const thumbs = Array.from(gallery.querySelectorAll('.ak-main-product__thumb'));

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        showStackItem(items, thumbs, index, gallery);
      });
    });

    gallery.querySelectorAll('[data-ak-gallery-dot]').forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = Number(dot.getAttribute('data-index') || 0);
        showStackItem(items, thumbs, idx, gallery);
      });
    });

    bindHeroSwipe(gallery, items, thumbs);
    bindThumbRail(gallery);
    warmupStackMedia(items, thumbs);

    // Desktop only: sync selected thumb while scrolling the stacked grid.
    // Mobile hides non-hero frames, so observing would always snap back to index 0.
    if (items.length > 1 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (isMobileGallery()) return;
          let best = null;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (!best || entry.intersectionRatio > best.intersectionRatio) {
              best = entry;
            }
          });
          if (!best) return;
          const index = items.indexOf(best.target);
          if (index >= 0) {
            selectThumb(thumbs, index);
            selectDots(gallery, index);
          }
        },
        { root: null, rootMargin: '-20% 0px -55% 0px', threshold: [0.15, 0.4, 0.7] }
      );
      items.forEach((item) => observer.observe(item));
    }

    return { gallery, slides: items, thumbs, mode: 'stack' };
  }

  function initCarouselGallery(gallery) {
    const slides = Array.from(gallery.querySelectorAll('.ak-main-product__slide'));
    const thumbs = Array.from(gallery.querySelectorAll('.ak-main-product__thumb'));

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        showSlide(gallery, slides, thumbs, index);
      });
    });

    const prev = gallery.querySelector('[data-ak-gallery-prev]');
    const next = gallery.querySelector('[data-ak-gallery-next]');
    if (prev) {
      prev.addEventListener('click', () => {
        showSlide(gallery, slides, thumbs, activeSlideIndex(slides) - 1);
      });
    }
    if (next) {
      next.addEventListener('click', () => {
        showSlide(gallery, slides, thumbs, activeSlideIndex(slides) + 1);
      });
    }

    bindThumbRail(gallery);

    return { gallery, slides, thumbs, mode: 'carousel' };
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-ak-gallery]');
    if (!gallery) return null;

    if (gallery.getAttribute('data-gallery-layout') === 'stack') {
      return initStackGallery(gallery);
    }
    return initCarouselGallery(gallery);
  }

  function initColorStrips(root) {
    root.querySelectorAll('[data-ak-color-strip]').forEach((strip) => {
      const row = strip.querySelector('[data-ak-color-row]');
      const prev = strip.querySelector('[data-ak-color-prev]');
      const next = strip.querySelector('[data-ak-color-next]');
      if (!row) return;

      const step = () => Math.max(80, Math.floor(row.clientWidth * 0.8));
      if (prev) {
        prev.addEventListener('click', () => {
          row.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', () => {
          row.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }
      row.addEventListener('scroll', () => updateColorStrip(strip), { passive: true });
      window.addEventListener('resize', () => updateColorStrip(strip));
      updateColorStrip(strip);
    });
  }

  function initSizeChart(root) {
    const dialog = root.querySelector('[data-ak-size-chart-dialog]');
    const openBtn = root.querySelector('[data-ak-size-chart-open]');
    if (!dialog || !openBtn || typeof dialog.showModal !== 'function') return;

    openBtn.addEventListener('click', () => {
      dialog.showModal();
      const closeBtn = dialog.querySelector('[data-ak-size-chart-close]');
      if (closeBtn) closeBtn.focus();
    });

    dialog.querySelectorAll('[data-ak-size-chart-close]').forEach((btn) => {
      btn.addEventListener('click', () => dialog.close());
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  function initStickyAtc(root, atc) {
    const sticky = root.querySelector('[data-ak-sticky-atc]');
    const stickyBtn = root.querySelector('[data-ak-atc-sticky]');
    if (!sticky || !stickyBtn || !atc) return { sync() {} };

    // Leave section layout so overflow-x: clip / ancestor transforms cannot trap fixed.
    if (sticky.parentElement !== document.body) {
      document.body.appendChild(sticky);
    }

    const atcInView = () => {
      const rect = atc.getBoundingClientRect();
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight || 0;
      return rect.bottom > 8 && rect.top < vh - 8;
    };

    const setVisible = (show) => {
      const on = !!show && isMobileGallery();
      sticky.classList.toggle('is-visible', on);
      sticky.removeAttribute('hidden');
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      document.body.classList.toggle('ak-atc-sticky', on);
    };

    const refresh = () => {
      setVisible(!atcInView());
    };

    const syncLabel = () => {
      stickyBtn.disabled = atc.disabled;
      stickyBtn.textContent = atc.textContent || 'Add to cart';
    };

    stickyBtn.addEventListener('click', () => {
      if (stickyBtn.disabled) return;
      atc.click();
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        () => {
          refresh();
        },
        { threshold: [0, 0.15, 0.5, 1], rootMargin: '0px' }
      );
      observer.observe(atc);
    }

    window.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', refresh);
      window.visualViewport.addEventListener('scroll', refresh);
    }
    document.querySelector('.page-wrapper')?.addEventListener('scroll', refresh, {
      passive: true,
    });

    syncLabel();
    refresh();

    return {
      sync() {
        syncLabel();
      },
    };
  }

  function init(root) {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.akMainProductReady === 'true') return;
    root.dataset.akMainProductReady = 'true';

    const galleryApi = initGallery(root);
    initColorStrips(root);
    initSizeChart(root);

    const jsonEl = root.querySelector('[data-ak-product-json]');
    const variantInput = root.querySelector('[data-ak-variant-id]');
    const atc = root.querySelector('[data-ak-atc]');
    const stickyApi = initStickyAtc(root, atc);
    if (!jsonEl || !variantInput) return;

    let product;
    try {
      product = JSON.parse(jsonEl.textContent || '{}');
    } catch (e) {
      return;
    }
    if (!product || !Array.isArray(product.variants)) return;

    const optionInputs = root.querySelectorAll(
      'input[data-option-position], .ak-pdp-colors[data-ak-color-mode="native"] input[type="radio"]'
    );
    const urlHasVariant = new URLSearchParams(window.location.search).has('variant');
    const liveRegion = root.querySelector('#ak-pdp-cart-live-region');
    const optionNames = Array.isArray(product.options) ? product.options : [];
    let lastAvailability = null;
    let didPreferAvailable = false;

    function optionNameAt(pos) {
      return String(optionNames[pos - 1] || '');
    }

    function isSizeTypeName(name) {
      const n = String(name || '').toLowerCase();
      return n.includes('size type') || n.includes('height') || n.includes('fit type');
    }

    function isColorName(name) {
      const n = String(name || '').toLowerCase();
      return n === 'color' || n === 'colour' || n.includes('colour') || n.includes('color');
    }

    function isSizeName(name) {
      const n = String(name || '').toLowerCase();
      return n.includes('size') && !isSizeTypeName(n);
    }

    function productHasSizeType() {
      return optionNames.some((name) => isSizeTypeName(name));
    }

    function selectedOptions() {
      const map = {};
      const current = product.variants.find((v) => String(v.id) === String(variantInput.value));
      if (current && Array.isArray(current.options)) {
        current.options.forEach((val, i) => {
          map[i + 1] = val;
        });
      }
      for (const input of root.querySelectorAll('input[data-option-position]:checked')) {
        const pos = Number(input.getAttribute('data-option-position'));
        if (pos) map[pos] = input.value;
      }
      return map;
    }

    function findVariant(map) {
      return product.variants.find((v) => {
        if (!Array.isArray(v.options)) return false;
        return v.options.every((val, i) => map[i + 1] == null || map[i + 1] === val);
      });
    }

    function skipPositionsFor(optionPos) {
      const skips = new Set();
      const name = optionNameAt(optionPos);
      if (isColorName(name)) {
        for (let i = 1; i <= optionNames.length; i += 1) {
          if (i !== optionPos) skips.add(i);
        }
        return skips;
      }
      if (isSizeTypeName(name)) {
        optionNames.forEach((n, i) => {
          if (isSizeName(n)) skips.add(i + 1);
        });
      }
      return skips;
    }

    function variantsForValue(optionPos, value, map) {
      const skips = skipPositionsFor(optionPos);
      return product.variants.filter((v) => {
        if (!Array.isArray(v.options)) return false;
        if (v.options[optionPos - 1] !== value) return false;
        return v.options.every((val, i) => {
          const pos = i + 1;
          if (pos === optionPos || skips.has(pos)) return true;
          if (map[pos] == null) return true;
          return val === map[pos];
        });
      });
    }

    function setSoldOutName(input, soldOut) {
      if (!(input instanceof HTMLInputElement)) return;
      const base = input.value;
      input.setAttribute('aria-label', soldOut ? `${base}, sold out` : base);
    }

    function applyVariantToInputs(variant) {
      if (!variant || !Array.isArray(variant.options)) return;
      variant.options.forEach((val, i) => {
        const pos = i + 1;
        const match = [...root.querySelectorAll(`input[data-option-position="${pos}"]`)].find(
          (input) => input.value === val
        );
        if (match) match.checked = true;
      });
    }

    function preferAvailableVariant() {
      if (didPreferAvailable || urlHasVariant) return;
      didPreferAvailable = true;
      const current = findVariant(selectedOptions());
      if (current && current.available) return;
      const firstAvail = product.variants.find((v) => v.available);
      if (firstAvail) applyVariantToInputs(firstAvail);
    }

    function applyOptionAvailability(map) {
      const hideInvalidSizes = productHasSizeType();
      for (const label of root.querySelectorAll('.ak-main-product__size')) {
        const input = label.querySelector('input[data-option-position]');
        if (!input) continue;
        const pos = Number(input.getAttribute('data-option-position'));
        const name = optionNameAt(pos);
        const matches = variantsForValue(pos, input.value, map);
        const exists = matches.length > 0;
        const available = matches.some((v) => v.available);
        const hide = hideInvalidSizes && isSizeName(name) && !exists;
        label.classList.toggle('is-unavailable', hide);
        label.hidden = hide;
        label.classList.toggle('is-soldout', !hide && !available);
        input.classList.remove('visually-hidden');
        input.classList.add('ak-visually-hidden-input');
        if (!label.querySelector('.ak-main-product__size-strike')) {
          const strike = document.createElement('span');
          strike.className = 'ak-main-product__size-strike';
          strike.setAttribute('aria-hidden', 'true');
          label.appendChild(strike);
        }
        setSoldOutName(input, !available);
      }

      for (const label of root.querySelectorAll(
        '.ak-pdp-colors[data-ak-color-mode="native"] .ak-pdp-color-tile'
      )) {
        const input = label.querySelector('input[data-option-position]');
        if (!input) continue;
        const pos = Number(input.getAttribute('data-option-position'));
        const matches = variantsForValue(pos, input.value, map);
        const available = matches.some((v) => v.available);
        label.classList.toggle('is-soldout', !available);
        input.classList.remove('visually-hidden');
        input.classList.add('ak-visually-hidden-input');
        setSoldOutName(input, !available);
      }
    }

    function revealHiddenSelection() {
      let moved = false;
      const groups = new Map();
      for (const input of root.querySelectorAll('.ak-main-product__size input[data-option-position]')) {
        const pos = input.getAttribute('data-option-position');
        if (!groups.has(pos)) groups.set(pos, []);
        groups.get(pos).push(input);
      }
      for (const inputs of groups.values()) {
        const checked = inputs.find((input) => input.checked);
        const checkedLabel = checked?.closest('.ak-main-product__size');
        if (!checkedLabel || !checkedLabel.hidden) continue;
        const next =
          inputs.find((input) => {
            const label = input.closest('.ak-main-product__size');
            return label && !label.hidden && !label.classList.contains('is-soldout');
          }) ||
          inputs.find((input) => {
            const label = input.closest('.ak-main-product__size');
            return label && !label.hidden;
          });
        if (next && next !== checked) {
          next.checked = true;
          moved = true;
        }
      }
      return moved;
    }

    function sync() {
      preferAvailableVariant();
      let map = selectedOptions();
      applyOptionAvailability(map);
      if (revealHiddenSelection()) {
        map = selectedOptions();
        applyOptionAvailability(map);
      }
      const variant = findVariant(map) || product.variants[0];
      if (!variant) return;
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
      if (atc) {
        atc.disabled = !variant.available;
        atc.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      if (stickyApi && typeof stickyApi.sync === 'function') stickyApi.sync();
      if (liveRegion && lastAvailability !== variant.available) {
        liveRegion.textContent = variant.available ? '' : 'Sold out';
        lastAvailability = variant.available;
      }
      root.querySelectorAll('.ak-main-product__size').forEach((label) => {
        const input = label.querySelector('input');
        label.classList.toggle('is-selected', !!(input && input.checked));
      });
      root.querySelectorAll('.ak-pdp-colors[data-ak-color-mode="native"] .ak-pdp-color-tile').forEach((label) => {
        const input = label.querySelector('input');
        label.classList.toggle('is-selected', !!(input && input.checked));
      });
      const colorValue = root.querySelector('[data-ak-color-value]');
      const colorChecked = root.querySelector(
        '.ak-pdp-colors[data-ak-color-mode="native"] input[type="radio"]:checked'
      );
      if (colorValue && colorChecked) colorValue.textContent = colorChecked.value;

      if (variant.featured_media && galleryApi) {
        const id = String(variant.featured_media.id);
        const { slides, thumbs, gallery, mode } = galleryApi;
        let matchedIndex = -1;
        slides.forEach((slide, i) => {
          if (slide.getAttribute('data-media-id') === id) matchedIndex = i;
        });
        if (matchedIndex >= 0) {
          if (mode === 'stack') {
            showStackItem(slides, thumbs, matchedIndex, gallery);
          } else {
            showSlide(gallery, slides, thumbs, matchedIndex);
          }
        }
      }
    }

    optionInputs.forEach((input) => {
      input.addEventListener('change', sync);
    });
    sync();
  }

  function boot() {
    document.querySelectorAll('[data-ak-main-product]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
